import * as Sentry from "@sentry/react";

import {
  scrubBreadcrumb,
  scrubEvent,
  scrubRecordingEvent,
  scrubSpan,
  scrubUrl,
} from "./sentry-scrub";

type Environment = "development" | "staging" | "production" | string;

type SampleDefaults = {
  traces: number;
  replaysSession: number;
  replaysOnError: number;
};

let initialized = false;

function parseRate(value: string | undefined, fallback: number): number {
  if (value === undefined || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1) return fallback;
  return n;
}

function resolveDefaults(environment: Environment): SampleDefaults {
  switch (environment) {
    case "production":
      return { traces: 0.1, replaysSession: 0.05, replaysOnError: 1.0 };
    case "staging":
      return { traces: 0.5, replaysSession: 0.2, replaysOnError: 1.0 };
    default:
      return { traces: 1.0, replaysSession: 1.0, replaysOnError: 1.0 };
  }
}

export function initSentry(): void {
  if (initialized) return;

  // Set only in local .env files so local errors never reach Sentry;
  // staging/production builds never define it.
  if (import.meta.env.VITE_SENTRY_DISABLED === "true") {
    console.log("[Sentry] VITE_SENTRY_DISABLED=true; skipping Sentry.init");
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.log("[Sentry] VITE_SENTRY_DSN not set; skipping Sentry.init");
    }
    return;
  }

  const environment: Environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    (import.meta.env.DEV ? "development" : "production");
  const defaults = resolveDefaults(environment);
  const isDev = import.meta.env.DEV;

  Sentry.init({
    dsn,
    environment,
    sendDefaultPii: isDev,
    tracesSampleRate: parseRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
      defaults.traces,
    ),
    replaysSessionSampleRate: parseRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as
        string | undefined,
      defaults.replaysSession,
    ),
    replaysOnErrorSampleRate: parseRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as
        string | undefined,
      defaults.replaysOnError,
    ),
    enableLogs: true,
    tracePropagationTargets: [
      "localhost",
      /.*ngrok-free\.dev/,
      /^https:\/\/server-evooluir\.teampumpkin\.com/,
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        beforeAddRecordingEvent: scrubRecordingEvent,
      }),
    ],
    beforeBreadcrumb: (breadcrumb) =>
      scrubBreadcrumb(
        breadcrumb as Parameters<typeof scrubBreadcrumb>[0],
      ) as typeof breadcrumb,
    beforeSend: (event) =>
      scrubEvent(event as Parameters<typeof scrubEvent>[0]) as typeof event,
    beforeSendTransaction: (event) =>
      scrubEvent(event as Parameters<typeof scrubEvent>[0]) as typeof event,
    beforeSendSpan: scrubSpan,
  });
  // replay_event (urls, request.url) skips beforeSend but still runs event processors.
  Sentry.addEventProcessor((event) =>
    event.type === "replay_event"
      ? (scrubEvent(event as Parameters<typeof scrubEvent>[0]) as typeof event)
      : event,
  );

  initialized = true;
}

export type SentryAppUser = { id: string; role?: string } | null;

export function setSentryUser(user: SentryAppUser): void {
  if (!initialized) return;
  if (user) {
    Sentry.setUser({ id: user.id, segment: user.role });
  } else {
    Sentry.setUser(null);
  }
}

export function setSentryLocale(locale: string): void {
  if (!initialized) return;
  Sentry.setTag("locale", locale);
}

export type ApiCaptureContext = {
  url: string;
  method: string;
  status?: number;
};

const SKIP_CAPTURE_PATHS = new Set(["/user-app-usage/track"]);

export function shouldCaptureApiError(ctx: ApiCaptureContext): boolean {
  if (SKIP_CAPTURE_PATHS.has(ctx.url)) return false;
  if (ctx.status === undefined) return true; // network / parse error
  if (ctx.status === 401 || ctx.status === 403) return false;
  if (ctx.status >= 500) return true;
  return false;
}

export function captureApiError(error: unknown, ctx: ApiCaptureContext): void {
  if (!initialized) return;
  if (!shouldCaptureApiError(ctx)) return;
  Sentry.captureException(error, {
    tags: {
      api_url: scrubUrl(ctx.url),
      api_method: ctx.method,
      api_status: ctx.status ? String(ctx.status) : "network_error",
    },
  });
}

const loggedStorageOps = new Set<string>();

/** Sentry log for a localStorage access the browser denied; once per op per page load. */
export function logStorageBlocked(op: string, error: unknown): void {
  if (!initialized || loggedStorageOps.has(op)) return;
  loggedStorageOps.add(op);
  Sentry.logger.warn("localStorage access blocked", {
    op,
    error:
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : String(error),
  });
}

export function __resetSentryForTests(): void {
  initialized = false;
  loggedStorageOps.clear();
}
